import type { Node } from 'prosemirror-model';

import {
  CONTENT_VARIANT_NODE_NAME,
  VARIABLE_CONTENT_NODE_NAME,
} from '../nodes/variable-content/constants';
import { getChannelId, parseDimensions } from '../nodes/variable-content/dimensions';
import { resolveChannelValueForVariableContent } from '../nodes/variable-content/utils';
import { headingToSlug } from './headingSlug';

export type Heading = {
  title: string;
  level: number;
  id: string;
};

export type GetHeadingsOptions = {
  selectedChannelId?: string;
};

function shouldIncludeHeading(doc: Node, pos: number, selectedChannelId?: string): boolean {
  if (!selectedChannelId) {
    return true;
  }

  const $pos = doc.resolve(pos);
  let variableContent: Node | null = null;
  let contentVariant: Node | null = null;

  for (let depth = $pos.depth; depth > 0; depth--) {
    const ancestor = $pos.node(depth);
    if (ancestor.type.name === CONTENT_VARIANT_NODE_NAME) {
      contentVariant = ancestor;
    }
    if (ancestor.type.name === VARIABLE_CONTENT_NODE_NAME) {
      variableContent = ancestor;
      break;
    }
  }

  if (!variableContent || !contentVariant) {
    return true;
  }

  const resolved = resolveChannelValueForVariableContent(variableContent, selectedChannelId);
  if (resolved == null) {
    return false;
  }

  const variantValue = getChannelId(parseDimensions(contentVariant.attrs.dimensions as string));

  return variantValue === resolved;
}

/**
 * Extracts all headings from a ProseMirror document.
 * Returns an array of heading objects with title, level, and unique id.
 *
 * @param doc ProseMirror document node
 * @returns Array of Heading objects
 */
export function getHeadings(doc: Node, options?: GetHeadingsOptions): Heading[] {
  const headings: Heading[] = [];
  const slugCounts: Record<string, number> = {}; // Track how many times each slug has been used
  // slug counts are used to ensure that headings with the same text have unique ids

  doc.descendants((node, pos) => {
    if (node.type.name === 'heading') {
      const text = node.textContent;
      const baseId = headingToSlug(text);
      const count = slugCounts[baseId] ?? 0;
      const id = count > 0 ? headingToSlug(text, count) : baseId;

      slugCounts[baseId] = count + 1;

      if (!shouldIncludeHeading(doc, pos, options?.selectedChannelId)) {
        return;
      }

      headings.push({
        title: text,
        level: node.attrs.level,
        id,
      });
    }
  });

  return headings;
}

/**
 * Compares two heading arrays for equality.
 * Used to avoid unnecessary callbacks when headings haven't changed.
 */
export function headingsEqual(a: Heading[], b: Heading[]): boolean {
  if (a.length !== b.length) return false;

  return a.every((h, i) => h.id === b[i].id && h.title === b[i].title && h.level === b[i].level);
}
